import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../../constants/constants";
import { Calendar, Clock, X } from "lucide-react";
import "../../../styles/zoom-override.css";

interface Meeting {
  id: string;
  topic: string;
  description: string;
  meetingId: string;
  joinUrl: string;
  zoomMeetingId?: string;
  zoomJoinUrl?: string;
  password: string;
  startTime: string;
  duration: number;
  timezone: string;
  isRecurring: boolean;
  creatorId: string;
  createdBy?: number;
  creator?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
    role?: string;
  };
  invitedUserIds: string[];
  createdAt: string;
  updatedAt: string;
}

const LiveDetail: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState("");
  const [useIframeMode] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const meetingContainerRef = useRef<HTMLDivElement>(null);

  // Charger les informations de l'utilisateur
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const firstName = user.firstName || "";
        const lastName = user.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        setUserName(fullName || "Utilisateur GuidePro");
      } catch (e) {
        console.error(
          "Erreur lors de la récupération des infos utilisateur:",
          e
        );
        setUserName("Utilisateur GuidePro");
      }
    }
  }, []);

  useEffect(() => {
    const fetchMeetingDetails = async () => {
      if (!meetingId) {
        setError("ID de réunion non valide");
        setLoading(false);
        return;
      }

      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await fetch(
          API_ENDPOINTS.MEETINGS.GET_MEETING_BY_ID(meetingId),
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des détails de la réunion: ${response.status}`
          );
        }

        const data = await response.json();
        setMeeting(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Une erreur est survenue"
        );
        console.error("Erreur de chargement des détails:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingDetails();
  }, [meetingId]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h${mins > 0 ? mins : ""}`;
    }
    return `${mins}min`;
  };

  const getCreatorName = (meeting: Meeting): string => {
    if (meeting.creator && meeting.creator.firstName) {
      return `${meeting.creator.firstName} ${
        meeting.creator.lastName || ""
      }`.trim();
    }

    if (meeting.creatorId) {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.id === meeting.creatorId || user.id === meeting.createdBy) {
            return user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : "Vous";
          }
        } catch (e) {
          console.error(
            "Erreur lors de la récupération des infos utilisateur:",
            e
          );
        }
      }
      return `Organisateur de réunion`;
    }

    return "Réunion Zoom";
  };

  // Fonction pour obtenir l'URL correcte pour l'iframe via le proxy
  const getIframeUrl = () => {
    if (!meeting) return "";

    const zoomMeetingId = meeting.zoomMeetingId || meeting.meetingId;
    const password = meeting.password || "";

    return `https://zoom.us/wc/join/${zoomMeetingId}?pwd=${encodeURIComponent(
      password
    )}&webinar=false&audio=true&video=true&web=true&meeting_result=false&un=${encodeURIComponent(
      userName || "Utilisateur GuidePro"
    )}`;
  };

  // Fonction pour basculer en mode plein écran
  const toggleFullscreen = () => {
    if (!isFullscreen && meetingContainerRef.current) {
      if (meetingContainerRef.current.requestFullscreen) {
        meetingContainerRef.current.requestFullscreen();
      }
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  // Écouter les changements d'état plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded max-w-md">
          <p className="font-bold">Erreur</p>
          <p>{error || "Réunion non trouvée"}</p>
        </div>
      </div>
    );
  }

  const defaultAvatar = "/images/avatar-placeholder.png";
  const creatorAvatar = meeting.creator?.profileImage
    ? `${import.meta.env.VITE_API_URL}/${meeting.creator.profileImage}`
    : defaultAvatar;
  const hostName = getCreatorName(meeting);

  return (
    <div className="min-h-screen bg-[#fefaf6]">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* En-tête */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {meeting.topic}
                </h1>
                <p className="text-gray-300 mt-2">{meeting.description}</p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="text-white hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Zone de la réunion */}
          <div ref={meetingContainerRef} className="w-full h-[600px] relative">
            {useIframeMode && meeting && (
              <>
                {/* Overlay de protection contre les erreurs CORS */}
                <div className="absolute inset-0 bg-transparent z-10 pointer-events-none"></div>

                <iframe
                  ref={iframeRef}
                  src={getIframeUrl()}
                  className="w-full h-full border-0"
                  allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; encrypted-media; geolocation"
                ></iframe>

                {/* Overlay avec bouton plein écran */}
                <div className="absolute top-4 right-4 z-50">
                  <button
                    onClick={toggleFullscreen}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isFullscreen ? "Quitter" : "Plein écran"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Informations de la réunion */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h2 className="text-lg font-semibold mb-4">
                    Informations de la réunion
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Calendar className="text-gray-500 mr-2" size={20} />
                      <span>{meeting && formatDate(meeting.startTime)}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="text-gray-500 mr-2" size={20} />
                      <span>{meeting && formatTime(meeting.startTime)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-2">Durée:</span>
                      <span>{meeting && formatDuration(meeting.duration)}</span>
                    </div>
                  </div>
                </div>

                {/* Informations de connexion */}
                {meeting?.password && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      Informations de connexion
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-500">
                          ID de réunion:
                        </span>
                        <p className="font-mono text-sm">{meeting.meetingId}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">
                          Mot de passe:
                        </span>
                        <p className="font-mono text-sm">{meeting.password}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Informations de l'organisateur */}
              {meeting && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h2 className="text-lg font-semibold mb-4">Organisateur</h2>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <img
                        src={creatorAvatar}
                        alt={getCreatorName(meeting)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-medium">{hostName}</p>
                      {meeting.creator?.email && (
                        <p className="text-gray-500 text-sm">
                          {meeting.creator.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveDetail;
